import { Injectable } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { switchMap, take } from "rxjs";
import { AuthorisationService } from "../authorisation/authorisation.service";
import { CommonTeamsService } from "../teams/common-teams.service";

@Injectable({
    providedIn: "root",
})
export class VisionAuthService {
    public static readonly EditVision = "editVision";
    public static readonly ReadVision = "readVision"; // this is to check for global or team access to VisionRead
    public static readonly ReadAnyVision = "readAnyVision"; // this is to check for any read access to vision in any team

    public constructor(private authorisationService: AuthorisationService, private teamsService: CommonTeamsService) { }

    public static registerAccess(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            VisionAuthService.EditVision,
            {
                requirePermissions: [
                    FeaturePermissionName.CompassVisionEdit,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            VisionAuthService.ReadVision,
            {
                requirePermissions: [
                    FeaturePermissionName.CompassVisionEdit,
                    FeaturePermissionName.CompassVisionRead,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            VisionAuthService.ReadAnyVision,
            {
                invokeToGetAccessVerifier: (injector) =>
                    getVerifyAccessToReadAnyVision(injector.get(CommonTeamsService), injector.get(VisionAuthService)),
            },
        );

        function getVerifyAccessToReadAnyVision(commonTeamsService: CommonTeamsService, visionAuthService: VisionAuthService) {
            return verifyAccessToReadAnyVision;

            async function verifyAccessToReadAnyVision(currentPerson: Person) {
                const activeTeams = await commonTeamsService.promiseToGetActiveTeamsForPersonId(currentPerson.personId);
                if (!activeTeams?.length) {
                    return Promise.reject();
                }

                const teamVisionAccesses = await Promise.all(
                    activeTeams.map((team) => visionAuthService.canViewTeamVision(team)),
                );

                return teamVisionAccesses.some((hasAccess) => hasAccess)
                    ? Promise.resolve()
                    : Promise.reject();
            }
        }
    }

    public canViewTeamVision(team: Team) {
        return this.authorisationService.promiseToGetHasAccess(
            VisionAuthService.ReadVision,
            team,
        );
    }

    public personCanEditVisionForTeamId(person: Person, teamId: number) {
        return this.authorisationService.personHasPermission(person, FeaturePermissionName.CompassVisionEdit, { teamId });
    }

    public canEditVisionForTeamId(teamId: number) {
        return this.teamsService.getTeamById(teamId).pipe(
            switchMap((team) => this.canEditTeamVision(team!)),
            take(1),
        );
    }

    private canEditTeamVision(team: Team) {
        return this.authorisationService.promiseToGetHasAccess(
            VisionAuthService.EditVision,
            team,
        );
    }
}
